import React from 'react';
import { Link } from 'react-router-dom';
import { List, Section, Strong, Table } from './prose';

const COLLECTED = [
  ['Your email address', 'To create your account, send you one-time login links and send emails about your account, such as confirming that it has been deleted.', 'Contract: we need it to provide the app you signed up for.'],
  ['Your training log', 'Date, session type, added weight, reps and grip for each set, how hard it was, how it felt, whether you had any discomfort, your session notes and your bodyweight. Used to show your progress table, strength chart, personal bests and plateau alerts.', 'Contract.'],
  ['Health notes', 'What you write in "Where and what kind?" when you log discomfort, and your check-in answer to "Anything getting in the way of showing up consistently?". Only stored if you tick the consent box above those fields.', 'Your explicit consent (UK GDPR Articles 6(1)(a) and 9(2)(a)).'],
  ['Weekly check-ins', 'How many sessions you completed and how your energy and recovery feel.', 'Contract.'],
  ['Account activity', 'When you joined, how many sessions you have logged, the dates of your first and last sessions and last check-in, whether you joined the priority list, and which one-off messages you have already seen.', 'Contract.'],
  ['Consent record', 'Whether you agreed to us storing health notes, and when you agreed or withdrew.', 'Legal obligation: we must be able to show your consent.'],
  ['Login links', 'A scrambled copy of each one-time link (never the link itself), the email it was sent to and when it expires.', 'Contract.'],
  ['Technical data', 'Your IP address, browser type and the pages you request, which our hosting and network providers process to deliver the app and keep it secure.', 'Legitimate interests: running a secure service.'],
];

const PROVIDERS = [
  ['Emergent', 'Hosts the app and its database. Processes all of the data above.', 'Emergent states that it processes data in the United States and India.'],
  ['MongoDB Atlas', 'The database service Emergent uses to store the data above.', 'Operated for us through Emergent.'],
  ['Cloudflare', 'The network that delivers app.coachrands.com. Processes your IP address, browser details and the pages you request.', 'Worldwide network.'],
  ['Resend', 'Sends your login links and account emails. Receives your email address and the content of those emails.', 'Our sending domain uses Resend\'s Ireland (EU) region. Resend is a US company.'],
];

const STORAGE = [
  ['cr_free_session', 'Cookie', 'Keeps you signed in. Lasts 180 days and renews each time you use the app.', 'Essential'],
  ['cr_cookie_consent', 'Local storage', 'Remembers whether you accepted or rejected optional cookies.', 'Essential'],
  ['free_signed_in, free_email', 'Local storage', 'Remembers that you are signed in, so the app opens straight to your dashboard. Cleared when you sign out or delete your account.', 'Essential'],
  ['free_hint_inapp_dismissed, free_hint_install_dismissed', 'Local storage', 'Remembers that you closed a tip about installing the app. Only set when you close it.', 'Functional, set at your request'],
  ['free_six_trigger_moment', 'Session storage', 'Shows a one-off message after your sixth session. Cleared when you close the tab.', 'Essential'],
  ['__emg_vid (1 year), __emg_sid (30 minutes)', 'Cookies', 'Set by our hosting provider, Emergent, before our app loads, so our cookie banner cannot block them. Emergent has not yet told us what they are used for. We will update this notice when it does.', 'Set by our host'],
];

export default function PrivacyContent() {
  return (
    <div data-testid="privacy-content">
      <Section title="Who we are">
        <p>
          The Coach Rands app at app.coachrands.com is run by <Strong>Randal Harrison, trading as Coach Rands</Strong>,
          a sole trader in the UK. Randal Harrison is the data controller for the personal data described in this
          notice.
        </p>
        <p>Contact: <Strong>[CONTACT EMAIL]</Strong></p>
      </Section>

      <Section title="What this notice covers">
        <p>
          This notice covers the free Coach Rands tracker and the free strength calculator. The paid coaching
          programme is not open to the public.
        </p>
        <p>
          The strength calculator works entirely in your browser. What you type into it is not sent to us and is not
          stored.
        </p>
      </Section>

      <Section title="What we collect and why">
        <Table head={['Data', 'What it is and why we use it', 'Lawful basis']} rows={COLLECTED} testid="privacy-collected-table" />
      </Section>

      <Section title="Health information and your consent">
        <p>
          Details about pain, injury or health are special category data under UK GDPR. We only store what you write
          in the discomfort and check-in fields if you tick the box that says you agree. If you leave it unticked,
          those fields stay switched off.
        </p>
        <p>
          You can withdraw your consent at any time by unticking the box. When you do, we delete every discomfort
          note and check-in answer you have written. Your sessions stay.
        </p>
        <p>
          Your session notes field is not covered by this consent, so please do not put details about pain, injury or
          health there.
        </p>
      </Section>

      <Section title="What we do not do">
        <List
          items={[
            'We do not sell your data or share it with advertisers.',
            'We do not send your data to any artificial intelligence (AI) service.',
            'We do not run analytics or advertising trackers. If we ever add an optional tool such as analytics, it will only run if you click Accept on our cookie banner.',
            'Our fonts are served from our own domain, so loading the app does not contact Google.',
          ]}
        />
      </Section>

      <Section title="Who we share it with">
        <p>We use these service providers. Each only processes your data to provide its service to us.</p>
        <Table head={['Provider', 'What it does and receives', 'Where']} rows={PROVIDERS} testid="privacy-providers-table" />
        <p>
          The Technique Guide links to a YouTube video. Nothing is sent to YouTube unless you click the link, and
          YouTube&apos;s own privacy policy applies once you do.
        </p>
      </Section>

      <Section title="Data stored outside the UK">
        <p>
          Some of our providers process data outside the UK, including in the United States and India. You can ask us
          for details of the safeguards that apply by emailing [CONTACT EMAIL].
        </p>
      </Section>

      <Section title="How long we keep it">
        <List
          items={[
            'Your account, training log, check-ins and consent record: until you delete your account.',
            'Health notes: until you untick the consent box, delete the session, or delete your account.',
            'Login links: deleted automatically as soon as they are used or expire, 30 minutes after they are sent.',
            'Emails we send: Resend keeps a record of each email under its own retention policy.',
            'Technical logs: kept by our hosting and network providers under their own retention policies.',
          ]}
        />
        <p>
          When you delete your account, we delete it from our database straight away. Copies may remain in our
          providers&apos; backups for a limited time before they are overwritten.
        </p>
      </Section>

      <Section title="Cookies and similar storage">
        <p>
          We only use the cookies and browser storage below. We do not use any optional cookies at the moment. You
          can change your choice at any time with the Cookie settings link at the bottom of each page.
        </p>
        <Table head={['Name', 'Type', 'Purpose', 'Category']} rows={STORAGE} testid="privacy-storage-table" />
      </Section>

      <Section title="Earlier test version of the coaching chat">
        <p>
          An early version of the paid coaching chat was briefly open to the public. It was not linked to an account
          and is now closed. Messages sent to it are still stored in our database and are no longer sent to any other
          service. If you used it and want your messages deleted, email [CONTACT EMAIL].
        </p>
      </Section>

      <Section title="Your rights">
        <p>Under UK GDPR you have the right to:</p>
        <List
          items={[
            'Access: ask for a copy of the personal data we hold about you.',
            'Correction: ask us to correct data that is wrong.',
            <>Deletion: delete your account and all of its data yourself from the <Link to="/free/account" className="text-white underline">Account page</Link>, or ask us to do it.</>,
            'Export: ask for your data in a commonly used, machine-readable format.',
            'Withdraw consent: untick the health consent box at any time.',
            'Object or restrict: ask us to stop or limit how we use your data.',
          ]}
        />
        <p>
          To use any of these rights, email [CONTACT EMAIL]. It is free and we will reply within one month.
        </p>
      </Section>

      <Section title="Complaints">
        <p>
          If you are unhappy with how we handle your data, please contact us first at [CONTACT EMAIL]. You also have
          the right to complain to the Information Commissioner&apos;s Office (ICO), the UK data protection regulator,
          at ico.org.uk or on 0303 123 1113.
        </p>
      </Section>

      <Section title="Changes to this notice">
        <p>If we change how we use your data, we will update this page and the date at the top.</p>
      </Section>
    </div>
  );
}
